/**
 * One vocabulary for every number on the Know Your Competitors page: labels,
 * plain-English definitions (shown as tooltips, and the same ones Mira uses),
 * formatting and good / needs work / poor tiers.
 */
import type { KycRank, KycSeo, KycSpeed } from "@/lib/api";

export type Tier = "good" | "ok" | "poor" | "none";

export const TIER_TEXT: Record<Tier, string> = {
  good: "text-emerald-400",
  ok: "text-amber-400",
  poor: "text-red-400",
  none: "text-muted-foreground/40",
};

export const TIER_BAR: Record<Tier, string> = {
  good: "bg-emerald-500",
  ok: "bg-amber-500",
  poor: "bg-red-500",
  none: "bg-transparent",
};

export interface MetricRow {
  domain: string;
  rank?: KycRank;
  speed?: KycSpeed;
  seo?: KycSeo;
}

export interface MetricDef {
  key: string;
  label: string;
  short: string;
  help: string;
  /** Higher is better (false for rank, LCP, CLS) */
  higherIsBetter: boolean;
  paidOnly?: boolean;
  value: (r: MetricRow) => number | null;
  format: (n: number) => string;
  tier: (n: number) => Tier;
  /** 0–100 for the inline bar, when a bar makes sense */
  bar?: (n: number) => number;
}

export function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 10_000) return `${Math.round(n / 1_000)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(Math.round(n));
}

const lighthouse = (n: number): Tier => (n >= 90 ? "good" : n >= 50 ? "ok" : "poor");

export const METRICS: MetricDef[] = [
  {
    key: "authority",
    label: "Authority",
    short: "Authority",
    help: "0–10: how strongly the rest of the web links to the site. Each extra point is much harder to earn than the last.",
    higherIsBetter: true,
    value: (r) => r.rank?.oprScore ?? null,
    format: (n) => n.toFixed(1),
    tier: (n) => (n >= 5 ? "good" : n >= 3 ? "ok" : "poor"),
    bar: (n) => Math.min(100, n * 10),
  },
  {
    key: "linking",
    label: "Linking domains",
    short: "Linking sites",
    help: "How many other websites link to this one (authority-weighted). The backlink number that matters — links from many different sites beat many links from one.",
    higherIsBetter: true,
    value: (r) => r.rank?.linkingDomains ?? null,
    format: compact,
    tier: () => "none",
  },
  {
    key: "rank",
    label: "Global rank",
    short: "Global rank",
    help: "Position among all websites by authority. Lower is better.",
    higherIsBetter: false,
    value: (r) => r.rank?.globalRank ?? null,
    format: (n) => `#${compact(n)}`,
    tier: () => "none",
  },
  {
    key: "mobile",
    label: "Mobile speed",
    short: "Mobile",
    help: "How fast the site loads on a phone, 0–100 (90+ good, 50–89 needs work, under 50 poor). The one search engines weigh most.",
    higherIsBetter: true,
    value: (r) => r.speed?.mobileScore ?? null,
    format: (n) => String(Math.round(n)),
    tier: lighthouse,
    bar: (n) => n,
  },
  {
    key: "desktop",
    label: "Desktop speed",
    short: "Desktop",
    help: "How fast the site loads on a computer, 0–100.",
    higherIsBetter: true,
    value: (r) => r.speed?.desktopScore ?? null,
    format: (n) => String(Math.round(n)),
    tier: lighthouse,
    bar: (n) => n,
  },
  {
    key: "lcp",
    label: "Largest paint (LCP)",
    short: "LCP",
    help: "Seconds until the main content shows on mobile. Good at 2.5s or less.",
    higherIsBetter: false,
    value: (r) => r.speed?.lcp ?? null,
    format: (n) => `${n.toFixed(1)}s`,
    tier: (n) => (n <= 2.5 ? "good" : n <= 4 ? "ok" : "poor"),
  },
  {
    key: "a11y",
    label: "Accessibility",
    short: "A11y",
    help: "0–100: how usable the site is for people using screen readers and other assistive tech.",
    higherIsBetter: true,
    value: (r) => r.speed?.mobileA11y ?? null,
    format: (n) => String(Math.round(n)),
    tier: lighthouse,
    bar: (n) => n,
  },
  {
    key: "traffic",
    label: "Organic traffic",
    short: "Search visits",
    help: "Estimated monthly visits from search engines.",
    higherIsBetter: true,
    paidOnly: true,
    value: (r) => r.seo?.organicTraffic ?? null,
    format: compact,
    tier: () => "none",
  },
  {
    key: "keywords",
    label: "Ranking keywords",
    short: "Keywords",
    help: "Estimated number of search terms the site ranks for.",
    higherIsBetter: true,
    paidOnly: true,
    value: (r) => r.seo?.organicKeywords ?? null,
    format: compact,
    tier: () => "none",
  },
];

export function median(values: (number | null)[]): number | null {
  const v = values.filter((x): x is number => x != null).sort((a, b) => a - b);
  if (!v.length) return null;
  const m = Math.floor(v.length / 2);
  return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
}

/** "ahead" / "behind" vs a benchmark, respecting the metric's direction. */
export function standing(def: MetricDef, value: number | null, benchmark: number | null): "ahead" | "behind" | "even" | null {
  if (value == null || benchmark == null) return null;
  const diff = value - benchmark;
  const scale = Math.max(Math.abs(benchmark), 1);
  if (Math.abs(diff) / scale < 0.05) return "even";
  return (diff > 0) === def.higherIsBetter ? "ahead" : "behind";
}

export const cleanHost = (d: string | null | undefined) =>
  d ? d.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0].trim() : null;

export const RELATIONSHIP: Record<"high" | "medium" | "low", { label: string; dot: string; pill: string }> = {
  high: { label: "Direct", dot: "bg-red-500", pill: "border-red-500/20 bg-red-500/10 text-red-400" },
  medium: { label: "Adjacent", dot: "bg-amber-500", pill: "border-amber-500/20 bg-amber-500/10 text-amber-400" },
  low: { label: "Indirect", dot: "bg-zinc-500", pill: "border-zinc-500/20 bg-zinc-500/10 text-zinc-400" },
};
